import type { JobData } from '@/domains/jobs/hooks/useJobData';
import { usePermitStatusList } from '@/domains/jobs/hooks/usePermitStatusList';
import { useUpdateJobDetail } from '@/domains/jobs/hooks/useUpdateJobDetail';
import { showSuccessToast } from '@/shared/notifications/toast-utils';
import { Checkbox } from '@/shared/ui/checkbox';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import type { PermitStatusItem } from '@skewb-works/contracts';
import { ShieldCheck } from 'lucide-react';

const NO_STATUS_VALUE = 'none';

export function PermitCard({ jobData }: { jobData: JobData }) {
  const numericJobId = Number(jobData.id);
  const canSave = Number.isFinite(numericJobId) && numericJobId > 0;
  const updateMutation = useUpdateJobDetail(canSave ? numericJobId : 0);
  const { data: permitStatuses, isLoading: statusesLoading } = usePermitStatusList();

  const permitRequired = jobData.permitRequired ?? false;
  const reinstatementRequired = jobData.reinstatementRequired ?? false;
  const isBusy = updateMutation.isPending || !canSave;

  const handlePermitRequiredChange = async (checked: boolean) => {
    if (!canSave) return;
    try {
      await updateMutation.mutateAsync({ permitRequired: checked });
      showSuccessToast('Permit required updated');
    } catch {
      // Global mutation error handler in query-client shows a toast.
    }
  };

  const handleReinstatementRequiredChange = async (checked: boolean) => {
    if (!canSave) return;
    try {
      await updateMutation.mutateAsync({ reinstatementRequired: checked });
      showSuccessToast('Reinstatement required updated');
    } catch {
      // Global mutation error handler in query-client shows a toast.
    }
  };

  const handleStatusChange = async (value: string) => {
    if (!canSave) return;
    const permitStatusId = value === NO_STATUS_VALUE ? null : Number(value);
    try {
      await updateMutation.mutateAsync({ permitStatusId });
      showSuccessToast('Permit status updated');
    } catch {
      // Global mutation error handler in query-client shows a toast.
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-orange-50 dark:bg-orange-950 flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-orange-600 dark:text-orange-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Permit &amp; Reinstatement</h3>
          <p className="text-xs text-muted-foreground">Permit requirements and status</p>
        </div>
      </div>

      {/* TODO(WRK-4960 role-gating): editing is open to any authenticated user until
          the planner/scheduler/LDP Field Engineer Cognito groups exist. Gate the controls below once roles are available. */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Checkbox
            id="permit-required"
            checked={permitRequired}
            disabled={isBusy}
            onCheckedChange={(checked) => handlePermitRequiredChange(checked === true)}
          />
          <Label htmlFor="permit-required" className="cursor-pointer">
            Permit Required
          </Label>
        </div>

        <div className="flex items-center gap-3">
          <Checkbox
            id="reinstatement-required"
            checked={reinstatementRequired}
            disabled={isBusy}
            onCheckedChange={(checked) => handleReinstatementRequiredChange(checked === true)}
          />
          <Label htmlFor="reinstatement-required" className="cursor-pointer">
            Reinstatement Required
          </Label>
        </div>

        <div>
          <div className="text-xs text-muted-foreground mb-1">Permit Status</div>
          <Select
            value={
              jobData.permitStatusId != null ? String(jobData.permitStatusId) : NO_STATUS_VALUE
            }
            onValueChange={handleStatusChange}
            disabled={statusesLoading || isBusy}
          >
            <SelectTrigger className="w-full h-9">
              <SelectValue placeholder={statusesLoading ? 'Loading...' : 'Select permit status'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_STATUS_VALUE}>Not set</SelectItem>
              {(permitStatuses ?? []).map((status: PermitStatusItem) => (
                <SelectItem key={status.id} value={String(status.id)}>
                  {status.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
